import { useState, useEffect } from "react";

/**
 * Hook to fetch content from the /api/content endpoint
 * @param slug - The Webflow collection slug to fetch
 * @returns Object containing the content items, loading state, and error state
 */
export function useContent<T = Record<string, unknown>>(slug: string | null) {
  const [content, setContent] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!slug) {
      setContent([]);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchContent = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/content?slug=${encodeURIComponent(slug)}`);

        if (!response.ok) {
          // If 404, it means no content exists for this slug - not an error
          if (response.status === 404) {
            setContent([]);
            return;
          }

          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Failed to fetch content: ${response.statusText}`);
        }

        const data = await response.json();
        setContent(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Unknown error occurred"));
        setContent([]);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [slug]);

  return { content, loading, error };
}
